import { Flex, InputNumber, Select, Text, TimePicker } from "@/atoms"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import { CalendarOutlined, InfoCircleOutlined } from "@/icons"
import { Form, Rules } from "@/organisms/Form"
import { FREQUENCY } from "@/pages/ReportsPage/const"
import { WeekDayPicker } from "./WeekDayPicker"

import styles from "./ReportFrequencySectionForm.module.scss"

import type { ReportFrequencySectionFormProps } from "."

export const ReportFrequencySectionForm = (
  props: ReportFrequencySectionFormProps
) => {
  const { form, t: tForm } = props
  const keyPrefix = "sections.frequency"
  const { t } = useTranslation("translation")
  const every = Form.useWatch("every", form)
  const unit = Form.useWatch("unit", form)
  const rules = [Rules.getRequiredFieldRule(t)]
  const showFirstDayOfMonthQuarter = unit === "quarter" || unit === "month"

  return (
    <Form.Section.RadioGroupTemplate
      sectionId="frequency"
      form={form}
      Icon={CalendarOutlined}
      fieldName="frequency"
      title={tForm(`${keyPrefix}.title`)}
      layout="horizontal"
      items={[
        { label: tForm(`${keyPrefix}.once`), value: FREQUENCY.ONCE },
        {
          label: tForm(`${keyPrefix}.repeating`),
          value: FREQUENCY.REPEATING,
          children: (
            <Flex vertical gap={12} className={styles.wrapper}>
              <Flex align="start" gap={12}>
                <Flex>
                  <Form.Item
                    colon={false}
                    name="every"
                    layout="horizontal"
                    label="Repeat every"
                  >
                    <InputNumber disabled className={styles.numberInput} />
                  </Form.Item>
                </Flex>
                <Flex className={styles.unitField}>
                  <Form.Item name="unit" layout="horizontal" rules={rules}>
                    <Select
                      options={["day", "week", "month", "quarter"].map(
                        (value) => ({
                          label: capitalizeFirstLetter(
                            t(`general.${value}`, { count: every || 0 })
                          ),
                          value
                        })
                      )}
                      onChange={(unit) => {
                        const days = form.getFieldValue("days")

                        form.setFieldsValue({
                          unit,
                          days: days || { 1: true }
                        })
                      }}
                    />
                  </Form.Item>
                </Flex>
              </Flex>
              {unit === "week" && <WeekDayPicker />}
              <Flex>
                <Form.Item
                  colon={false}
                  label="On"
                  name="time"
                  layout="horizontal"
                  rules={rules}
                  required={false}
                >
                  <TimePicker allowClear={false} format="HH:mm" />
                </Form.Item>
              </Flex>
              {showFirstDayOfMonthQuarter && (
                <Flex align="center" gap={4}>
                  <Text type="secondary">
                    <InfoCircleOutlined />
                  </Text>
                  <Text type="secondary" className={styles.labelInfo}>
                    {tForm("fields.firstDayOfPeriod", {
                      period: t(`general.${unit}_one`)
                    })}
                  </Text>
                </Flex>
              )}
            </Flex>
          )
        }
      ]}
    />
  )
}
